import { Button, Select, Space, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector, useAuth } from '../../store/hooks';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import { SettingOutlined } from '@ant-design/icons';
import CompanySettingsModal from './CompanySettingsModal';
import { Company, setCompanies, setSelectedCompany } from '../../store/companySlice';
import JobDrawer from '../Job/JobDawer';


const CompanySelect: React.FC = () => {
    const dispatch = useAppDispatch()
    const companies = useAppSelector((state) => state.companies.companies)
    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany)
    const [selectedValue, setSelectedValue] = useState<string | null>(null)

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const { currentUser, loading, claims } = useAuth()
    const userId = currentUser?.uid

    useEffect(() => {
        if (!userId) {
            console.error("User is not authenticated");
            return;
        }

        const hasWildcard = (claims?.companyAccess?.['*'] ?? 0) >= 10;
        const isSuperadmin = claims?.superadmin === true;

        // Wildcard/superadmin: load all non-deleted companies
        if (hasWildcard || isSuperadmin) {
            const q = query(
                collection(db, "companies"),
                where("status", "!=", "delete")
            );
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const updatedEntities = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Company))
                dispatch(setCompanies(updatedEntities));
            }, (error) => {
                console.error("Error fetching real-time updates: ", error);
            });
            return () => unsubscribe();
        }

        // Otherwise, merge accessLevels + legacy userIds memberships
        const companiesMap = new Map<string, Company>();

        const applySnapshot = (snapshot: any) => {
            snapshot.docs.forEach((doc: any) => {
                const data = { id: doc.id, ...doc.data() } as Company;
                companiesMap.set(doc.id, data);
            });
            // Filter out deleted on the client to avoid multiple inequality filters
            const merged = Array.from(companiesMap.values()).filter(c => c.status !== 'delete');
            dispatch(setCompanies(merged));
        };

        const qAccess = query(
            collection(db, "companies"),
            where(`accessLevels.${userId}`, ">=", 10)
        );
        const qLegacy = query(
            collection(db, "companies"),
            where("userIds", "array-contains", userId)
        );

        const unsubscribeAccess = onSnapshot(qAccess, applySnapshot, (error) => {
            console.error("Error fetching accessLevels companies: ", error);
        });
        const unsubscribeLegacy = onSnapshot(qLegacy, applySnapshot, (error) => {
            console.error("Error fetching legacy companies: ", error);
        });

        return () => {
            unsubscribeAccess();
            unsubscribeLegacy();
        };
    }, [userId, claims?.companyAccess, claims?.superadmin, dispatch])

    useEffect(() => {
        if (companies && selectedCompany) {
            const updatedEntity = companies.find((sp) => sp.id === selectedCompany.id);
            if (updatedEntity) {
                dispatch(setSelectedCompany(updatedEntity));
            }
        }
    }, [companies, dispatch])

    const options = useMemo(() => {
        return companies.map((enteties) => ({
            value: enteties.id,
            label: enteties.name,
        }));
    }, [companies])

    useEffect(() => {
        if (selectedCompany) {
            setSelectedValue(selectedCompany.id);
        } else if (companies.length > 0) {
            dispatch(setSelectedCompany(companies[0]))
        } else {
            setSelectedValue(null);
        }
    }, [selectedCompany, companies, dispatch]);

    const handleSelect = (value: string) => {
        if (value !== selectedValue) {
            setSelectedValue(value)
            const selectedEntity = companies.find((entity) => entity.id === value)

            if (selectedEntity) dispatch(setSelectedCompany(selectedEntity))
        }
    }

    const companyAccess = claims?.companyAccess || {};
    const wildcardLevel = companyAccess['*'] ?? 0;
    const selectedLevelFromClaims = selectedCompany ? (companyAccess[selectedCompany.id] ?? 0) : 0;
    const selectedLevelFromDoc = selectedCompany?.accessLevels?.[userId ?? ''] ?? 0;
    const selectedLevel = Math.max(selectedLevelFromClaims, selectedLevelFromDoc);
    const canManageCompanySettings = claims?.superadmin || wildcardLevel >= 80 || selectedLevel >= 80;

    return (
        <Space.Compact>
            <Select
                showSearch
                size='middle'
                style={{ width: 200 }}
                placeholder="Select a Company"
                optionFilterProp='label'
                options={options}
                loading={loading}
                onSelect={handleSelect}
                value={selectedValue}
                filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                }
            />
            {canManageCompanySettings ? (
                <>
                    <CompanySettingsModal open={isSettingsModalOpen} handleClose={() => setIsSettingsModalOpen(false)} />
                    <Tooltip title="Company Settings">
                        <Button icon={<SettingOutlined />} size='middle' onClick={() => setIsSettingsModalOpen(true)} />
                    </Tooltip>
                </>
            ) : null}
            <JobDrawer />
        </Space.Compact>
    )
}

export default CompanySelect;
